import { GeneDataMap, SearchMode, TableDataType } from '../_types';
import { Trie } from './Trie';

/**
 * Panel 모드에서 Exact Match 유전자 검색
 */
function handleExactMatch(
  termLower: string,
  geneDataMap: GeneDataMap,
  validGeneNameSet: Set<string>,
) {
  if (termLower in geneDataMap) {
    validGeneNameSet.add(termLower);
    // panel 검색에선 prefix 검색 불필요
  }
  // panel 검색에선 invalid gene 처리 불필요
}

/**
 * Genes 모드에서 Prefix Match 유전자 검색
 */
function handlePrefixMatch(
  term: string,
  termLower: string,
  geneTrie: Trie,
  validGeneNameSet: Set<string>,
  searchedPrefixes: string[],
  prefixMatchedGenes: string[],
  searchedInvalidGenes: string[],
) {
  const matchedGenes = geneTrie.search(term);

  // 유효하지 않은 유전자 이름인 경우
  if (matchedGenes.length === 0) {
    searchedInvalidGenes.push(term);
    return;
  }

  // 정확히 일치하는 경우 (exact match)
  if (
    matchedGenes.length === 1 &&
    matchedGenes[0].toLowerCase() === termLower
  ) {
    validGeneNameSet.add(termLower);
    return;
  }

  // 접두사 일치하는 경우 (prefix match)
  if (!searchedPrefixes.includes(term)) {
    searchedPrefixes.push(term);
  }

  matchedGenes.forEach(geneName => {
    const lowercaseGeneName = geneName.toLowerCase();
    validGeneNameSet.add(lowercaseGeneName);

    // 정확한 매치는 prefixMatchedGenes에 추가하지 않음
    if (lowercaseGeneName !== termLower) {
      prefixMatchedGenes.push(geneName);
    }
  });
}

/**
 * 검색어에 따른 유전자 데이터 필터링
 */
function filterGeneDataByValidGenes(
  validGeneNameSet: Set<string>,
  geneDataMap: GeneDataMap,
): TableDataType[] {
  const searchFilteredGeneData: TableDataType[] = [];

  validGeneNameSet.forEach(geneName => {
    if (geneName in geneDataMap) {
      searchFilteredGeneData.push(geneDataMap[geneName]);
    }
  });

  return searchFilteredGeneData;
}

export function getGeneNameDataBySearchTerm(
  searchTerm: string,
  geneDataMap: GeneDataMap,
  geneTrie: Trie,
  hasSearched: boolean,
  searchMode: SearchMode = 'Genes',
) {
  // 초기값 설정
  const searchedInvalidGenes: string[] = [];
  const validGeneNameSet: Set<string> = new Set();
  const prefixMatchedGenes: string[] = [];
  const searchedPrefixes: string[] = [];

  // 검색 조건이 충족되지 않으면 빈 결과 반환 (early return).
  // 데이터가 아직 로드되지 않았으면(geneDataMap 비어있음) 검색하지 않는다.
  // 그러지 않으면 빈 데이터에 대해 유효한 유전자가 invalid 로 오판된다.
  const isGeneDataEmpty = Object.keys(geneDataMap).length === 0;
  if (!searchTerm.trim() || !hasSearched || isGeneDataEmpty) {
    return {
      searchFilteredGeneData: [],
      searchedInvalidGenes,
      prefixMatchedGenes,
      searchedPrefixes,
    };
  }

  // 검색어를 쉼표나 공백으로 분리하여 개별 유전자 이름으로 처리
  const terms = searchTerm
    .split(/[,\s]+/)
    .map(term => term.trim())
    .filter(term => term.length > 0);

  // 각 검색어의 소문자 버전을 기록하여 중복 검사 방지
  const processedTermsLowerSet = new Set<string>();

  // 각 검색어에 대해 검색 수행
  terms.forEach(term => {
    const termLower = term.toLowerCase();

    // 이미 처리된 용어(대소문자 무시)는 건너뜀
    if (processedTermsLowerSet.has(termLower)) {
      return;
    }
    processedTermsLowerSet.add(termLower);

    if (searchMode === 'Panel') {
      handleExactMatch(termLower, geneDataMap, validGeneNameSet);
    } else {
      handlePrefixMatch(
        term,
        termLower,
        geneTrie,
        validGeneNameSet,
        searchedPrefixes,
        prefixMatchedGenes,
        searchedInvalidGenes,
      );
    }
  });

  return {
    // 유효한 유전자 이름이 있는 경우 데이터 필터링
    searchFilteredGeneData: filterGeneDataByValidGenes(
      validGeneNameSet,
      geneDataMap,
    ),
    searchedInvalidGenes,
    prefixMatchedGenes,
    searchedPrefixes,
  };
}
